'use client'

import type { CSSProperties, ReactNode } from 'react'
import Link from 'next/link'
import type { Route } from 'next'
import { useRouter } from 'next/navigation'
import { useClerk, useUser } from '@clerk/nextjs'
import { useEffect } from 'react'
import {
  FileText,
  HelpCircle,
  Home,
  LayoutGrid,
  LineChart,
  LogOut,
  MoreVertical,
  Repeat,
  Target,
  User,
  Sparkles,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Logo } from '@/components/brand/logo'
import { ModeToggle } from '@/components/mode-toggle'
import { NotificationBell } from '@/components/notification-bell'
import { routes } from '@/config/routes'
import { SERVICE_TYPE_TO_FLOW } from '@/features/cases'
import { useMyInvestorCases, useMyInvestorProfile } from '@/features/investors'
import { DashboardTour } from '@/features/investor/components/tour/dashboard-tour'
import { tryAutoOpenDashboardTour, useTourStore } from '@/features/investor/store/tour-store'
import { useMarketTicker, type MarketTickerItem } from '@/features/market-data'
import { formatCurrency } from '@/lib/format/currency'
import { useThemeStore } from '@/stores/theme-store'
import '@/components/patterns/dashboard-shell.css'

export type InvestorTab = 'home' | 'portfolio' | 'audit' | 'trades' | 'build' | 'profile'

/** Gold/silver are INR amounts (converted from a USD spot price) — everything else (indices,
 * stocks, USD/INR) is a plain index/price/rate figure, not a currency amount to prefix with ₹. */
const CURRENCY_TICKER_KEYS = new Set(['gold', 'silver'])

const TICKER_NUMBER_FORMATTER = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

function formatTickerValue(item: MarketTickerItem): string {
  if (CURRENCY_TICKER_KEYS.has(item.key)) return formatCurrency(item.value)
  return TICKER_NUMBER_FORMATTER.format(item.value)
}

function formatTickerChange(changePercent: number): string {
  const sign = changePercent >= 0 ? '+' : '−'
  return `${sign}${Math.abs(changePercent).toFixed(2)}%`
}

const NAV_ICONS: Record<InvestorTab, typeof LayoutGrid> = {
  home: LayoutGrid,
  portfolio: LineChart,
  audit: FileText,
  trades: Repeat,
  build: Target,
  profile: User,
}

const TAB_TITLE: Record<InvestorTab, string> = {
  home: 'Dashboard',
  portfolio: 'Portfolio',
  audit: 'Audit & Prescription',
  trades: 'Trades',
  build: 'Goals',
  profile: 'Profile',
}

/** Every service tab is always visible — a tab for a service you don't own yet is how you
 * discover and request it, not something that only appears once you already have it. Each
 * page's own empty state carries the request CTA. */
const SERVICE_ITEMS: { key: InvestorTab; label: string; href: Route }[] = [
  { key: 'portfolio', label: 'Portfolio', href: routes.investor.portfolio as Route },
  { key: 'audit', label: 'Audit & Prescription', href: routes.investor.auditPrescription as Route },
  { key: 'trades', label: 'Trades', href: routes.investor.trades as Route },
  { key: 'build', label: 'Goals', href: routes.investor.build as Route },
]

const NAV_ITEMS: { key: InvestorTab; label: string; href: Route }[] = [
  { key: 'home', label: 'Dashboard', href: routes.investor.home as Route },
  ...SERVICE_ITEMS,
  { key: 'profile', label: 'Profile', href: routes.investor.profile as Route },
]

function getSidebarCookieDefault(): boolean {
  if (typeof document === 'undefined') return true
  const match = document.cookie.match(/(?:^|; )sidebar_state=([^;]+)/)
  return match ? match[1] === 'true' : true
}

function initials(name: string | null | undefined): string {
  if (!name) return 'I'
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('')
}

function InvestorUserMenu({ canExplore }: { canExplore: boolean }) {
  const router = useRouter()
  const { isMobile } = useSidebar()
  const { user } = useUser()
  const { signOut } = useClerk()
  const openTour = useTourStore((s) => s.openTour)

  const name = user?.fullName ?? 'Investor'
  const email = user?.primaryEmailAddress?.emailAddress ?? ''

  const handleSignOut = async () => {
    await signOut({ redirectUrl: '/login' })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton
          size="lg"
          className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground group-data-[collapsible=icon]:justify-center"
        >
          <Avatar className="h-8 w-8 shrink-0 rounded-lg">
            <AvatarFallback className="bg-primary text-primary-foreground rounded-lg text-[11px] font-bold">
              {initials(name)}
            </AvatarFallback>
          </Avatar>
          <span className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
            <span className="truncate font-medium">{name}</span>
            <span className="text-muted-foreground truncate text-xs">Member</span>
          </span>
          <MoreVertical className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
        side={isMobile ? 'bottom' : 'right'}
        align="end"
        sideOffset={4}
      >
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <Avatar className="h-8 w-8 rounded-lg">
              <AvatarFallback className="bg-primary text-primary-foreground rounded-lg text-[11px] font-bold">
                {initials(name)}
              </AvatarFallback>
            </Avatar>
            <span className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{name}</span>
              <span className="text-muted-foreground truncate text-xs">{email}</span>
            </span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => router.push(routes.investor.profile as Route)}>
          <User /> View Profile
        </DropdownMenuItem>
        {canExplore && (
          <DropdownMenuItem onClick={() => router.push(routes.investor.explore as Route)}>
            <Sparkles /> Explore other services
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={openTour}>
          <HelpCircle /> Take the tour
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onClick={() => void handleSignOut()}>
          <LogOut /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Backed by a server-polled, server-cached backend endpoint (`GET /market-data/ticker`) — this
 * data drives zero decisions anywhere in the app, it's purely decorative. A market-data problem
 * must never be visible here as an app error, spinner, or layout shift: before the first
 * successful fetch (or if the backend is unreachable and nothing has ever loaded), this renders
 * nothing at all rather than a skeleton or the old static placeholder; once data has loaded once,
 * React Query's previous-data retention keeps showing it through any later fetch failure. */
/** `dash-ticker-track`'s CSS animation has a fixed 26s duration, tuned for the design source's
 * original static 5-item list. The real feed can show up to 11 items — at a fixed duration, more
 * items means a longer/wider track scrolling past in the same 26s, i.e. visibly faster motion than
 * intended. Scaled by item count (same ~5.2s-per-item pace as the original 26s/5-items design) so
 * the scroll speed stays roughly constant regardless of how many sources are currently available. */
const TICKER_SECONDS_PER_ITEM = 26 / 5
const TICKER_MIN_DURATION_SECONDS = 15

function tickerAnimationDuration(itemCount: number): string {
  return `${Math.max(itemCount * TICKER_SECONDS_PER_ITEM, TICKER_MIN_DURATION_SECONDS)}s`
}

export function MarketTicker({ className }: { className?: string }) {
  const { data } = useMarketTicker()
  const items = data?.items ?? []
  if (items.length === 0) return null

  return (
    <div className={className} role="marquee" aria-label="Market ticker">
      <div
        className="dash-ticker-track flex whitespace-nowrap"
        style={{ animationDuration: tickerAnimationDuration(items.length) }}
      >
        {[...items, ...items].map((item, i) => (
          <span
            key={`${item.key}-${i}`}
            className="inline-flex items-center gap-1.5 border-r px-4 text-xs font-medium"
          >
            <span className="text-muted-foreground font-semibold tracking-wide">{item.label}</span>
            <span className="tabular-nums">{formatTickerValue(item)}</span>
            {item.changePercent !== null && (
              <span className={item.isUp ? 'text-green-600' : 'text-red-600'}>
                {formatTickerChange(item.changePercent)}
              </span>
            )}
          </span>
        ))}
      </div>
    </div>
  )
}

export function InvestorShell({
  active,
  title,
  description,
  actions,
  children,
}: {
  active: InvestorTab
  title?: ReactNode
  description?: ReactNode
  actions?: ReactNode
  children: ReactNode
}) {
  const router = useRouter()
  const { signOut } = useClerk()
  const theme = useThemeStore((s) => s.theme)
  const { data: investor } = useMyInvestorProfile()
  const { data: investorCases = [] } = useMyInvestorCases()
  const openTour = useTourStore((s) => s.openTour)

  const ownedFlows = new Set(investorCases.map((c) => SERVICE_TYPE_TO_FLOW[c.serviceType]))
  const canExplore = ownedFlows.size < SERVICE_ITEMS.length

  // First time this investor lands on any dashboard page, walk them through it once — never
  // again after that, unless they reopen it from the header icon or user menu.
  useEffect(() => {
    if (investor?.id) tryAutoOpenDashboardTour(investor.id)
  }, [investor?.id])

  return (
    <SidebarProvider
      defaultOpen={getSidebarCookieDefault()}
      // See the identical comment in `StaffShell`/`AdminShell` — pins the app root to the
      // viewport so only the inner content area scrolls, and makes `position: sticky` work for
      // descendants. `dark` itself is applied once, at `<html>`, not re-derived per surface here.
      className={cn(
        'text-foreground h-svh overflow-hidden',
        theme === 'dark' ? 'bg-background' : 'bg-muted',
      )}
      style={{ '--sidebar-width': '16rem', '--header-height': '3.5rem' } as CSSProperties}
    >
      <Sidebar collapsible="icon" variant="inset" className="hidden md:flex">
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild className="data-[slot=sidebar-menu-button]:p-1.5!">
                <Link href={routes.investor.home as Route} aria-label="Nivex home">
                  <Logo size={20} />
                  <span className="text-base font-semibold">Nivex</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarMenu>
              {NAV_ITEMS.map((item) => {
                const Icon = NAV_ICONS[item.key]
                return (
                  <SidebarMenuItem key={item.key}>
                    <SidebarMenuButton
                      asChild
                      isActive={active === item.key}
                      tooltip={item.label}
                      data-tour={`nav-${item.key}`}
                    >
                      <Link href={item.href}>
                        <Icon />
                        <span>{item.label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )
              })}
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip="Back to Home">
                <Link href={routes.splash as Route}>
                  <Home />
                  <span>Back to Home</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
          <InvestorUserMenu canExplore={canExplore} />
        </SidebarFooter>
      </Sidebar>

      <SidebarInset className="min-w-0 overflow-hidden">
        <header className="h-(--header-height) flex shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear">
          <div className="flex w-full min-w-0 items-center gap-1 px-4 lg:gap-2 lg:px-6">
            <SidebarTrigger className="-ml-1 hidden md:flex" />
            <Separator
              orientation="vertical"
              className="mx-2 hidden data-[orientation=vertical]:h-4 md:block"
            />

            <Link
              href={routes.investor.home as Route}
              aria-label="Nivex home"
              className="flex shrink-0 items-center gap-2 md:hidden"
            >
              <Logo size={20} />
              <span className="text-base font-semibold">Nivex</span>
            </Link>

            <h1 className="hidden shrink-0 text-base font-medium md:block">{TAB_TITLE[active]}</h1>

            <MarketTicker className="ml-4 hidden min-w-0 flex-1 overflow-hidden lg:block" />

            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground size-8"
                aria-label="Take the tour"
                data-tour="help"
                onClick={openTour}
              >
                <HelpCircle className="size-4" />
              </Button>
              <ModeToggle />
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button aria-label="Account menu" className="flex md:hidden">
                    <Avatar className="h-8 w-8 rounded-full">
                      <AvatarFallback className="bg-primary text-primary-foreground rounded-full text-xs font-bold">
                        {initials(investor?.name)}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => router.push(routes.investor.profile as Route)}>
                    <User /> View Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => router.push(routes.splash as Route)}>
                    <Home /> Back to Home
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={() => void signOut({ redirectUrl: '/login' })}
                  >
                    <LogOut /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <MarketTicker className="w-full overflow-hidden border-b lg:hidden" />

        <div className="dash-scroll-fade flex min-w-0 flex-1 flex-col overflow-y-auto">
          <div className="@container/main flex min-w-0 flex-1 flex-col">
            <div className="flex min-w-0 flex-col gap-4 px-4 py-4 pb-28 md:gap-6 md:py-6 md:pb-6 lg:px-6">
              {title || actions ? (
                <div className="flex flex-wrap items-start justify-between gap-3">
                  {title ? (
                    <div>
                      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
                      {description ? (
                        <p className="text-muted-foreground mt-1 text-sm">{description}</p>
                      ) : null}
                    </div>
                  ) : (
                    <div />
                  )}
                  {actions}
                </div>
              ) : null}
              {children}
            </div>
          </div>
        </div>
      </SidebarInset>

      <nav className="bg-background fixed bottom-4 left-1/2 z-30 flex h-[60px] w-[calc(100%-2rem)] max-w-[420px] -translate-x-1/2 items-center gap-0.5 rounded-[20px] border p-1.5 shadow-lg md:hidden">
        {NAV_ITEMS.map((item) => {
          const Icon = NAV_ICONS[item.key]
          const isActive = active === item.key
          const label = item.key === 'audit' ? 'Audit' : item.label
          return (
            <Link
              key={item.key}
              href={item.href}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'text-muted-foreground flex h-12 min-w-0 flex-1 items-center justify-center gap-1.5 overflow-hidden rounded-2xl transition-[flex,background-color,color] duration-200',
                isActive && 'bg-primary text-primary-foreground flex-[2]',
              )}
            >
              <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
              {isActive && <span className="truncate text-xs font-semibold">{label}</span>}
            </Link>
          )
        })}
      </nav>

      <DashboardTour />
    </SidebarProvider>
  )
}
